// Project Metadata Constants - Single source of truth for all dropdown options

// Industry options - supports custom entries via Combobox
export const INDUSTRY_OPTIONS = [
  { value: 'financial_services', label: 'Financial Services' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'technology', label: 'Technology' },
  { value: 'government', label: 'Government' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'retail', label: 'Retail' },
  { value: 'energy', label: 'Energy' },
  { value: 'education', label: 'Education' },
  { value: 'professional_services', label: 'Professional Services' },
  { value: 'telecommunications', label: 'Telecommunications' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'construction', label: 'Construction' },
  { value: 'defense', label: 'Defense' },
  { value: 'other', label: 'Other' },
] as const;

// Buyer types - affects evaluation criteria and readiness scoring
export const BUYER_TYPE_OPTIONS = [
  { value: 'regulated', label: 'Regulated', description: 'Subject to regulatory oversight (FCA, SEC, etc.)' },
  { value: 'enterprise', label: 'Enterprise', description: 'Large corporate with formal procurement' },
  { value: 'smb', label: 'SMB', description: 'Small/medium business with lighter process' },
  { value: 'government', label: 'Government', description: 'Public sector with strict compliance requirements' },
] as const;

// Opportunity types
export const OPPORTUNITY_TYPE_OPTIONS = [
  { value: 'RFP', label: 'RFP', description: 'Request for Proposal' },
  { value: 'RFQ', label: 'RFQ', description: 'Request for Quotation' },
  { value: 'RFI', label: 'RFI', description: 'Request for Information' },
  { value: 'renewal', label: 'Renewal', description: 'Contract renewal' },
  { value: 'expansion', label: 'Expansion / Upsell', description: 'Existing customer growth' },
  { value: 'sole_source', label: 'Sole-source', description: 'Non-competitive procurement' },
] as const;

// RFP source options
export const RFP_SOURCE_OPTIONS = [
  { value: 'procurement_portal', label: 'Procurement portal' },
  { value: 'client_direct', label: 'Client direct' },
  { value: 'partner_referral', label: 'Partner referral' },
  { value: 'framework', label: 'Framework' },
  { value: 'existing_customer', label: 'Existing customer' },
  { value: 'other', label: 'Other (specify)' },
] as const;

// Project status with editability rules - Unified statuses
export const PROJECT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active', editableFields: 'all' },
  { value: 'submitted', label: 'Submitted', editableFields: 'limited' },
  { value: 'won', label: 'Won', editableFields: 'read_only' },
  { value: 'lost', label: 'Lost', editableFields: 'read_only' },
  { value: 'no_decision', label: 'No Decision', editableFields: 'read_only' },
  { value: 'no_response', label: 'No Response', editableFields: 'read_only' },
  { value: 'withdrawn', label: 'Withdrawn', editableFields: 'read_only' },
] as const;

// Status editability matrix
export const STATUS_EDITABILITY: Record<string, {
  name: boolean;
  client: boolean;
  dealValue: boolean;
  dueDate: boolean;
  description: boolean;
  notes: boolean;
  other: boolean;
}> = {
  active: { name: true, client: true, dealValue: true, dueDate: true, description: true, notes: true, other: true },
  submitted: { name: false, client: false, dealValue: false, dueDate: true, description: true, notes: true, other: true },
  won: { name: false, client: false, dealValue: false, dueDate: false, description: false, notes: true, other: false },
  lost: { name: false, client: false, dealValue: false, dueDate: false, description: false, notes: true, other: false },
  no_decision: { name: false, client: false, dealValue: false, dueDate: false, description: false, notes: true, other: false },
  no_response: { name: false, client: false, dealValue: false, dueDate: false, description: false, notes: true, other: false },
  withdrawn: { name: false, client: false, dealValue: false, dueDate: false, description: false, notes: true, other: false },
};

// Internal priority levels
export const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'text-muted-foreground' },
  { value: 'medium', label: 'Medium', color: 'text-foreground' },
  { value: 'high', label: 'High', color: 'text-orange-500' },
  { value: 'strategic', label: 'Strategic', color: 'text-primary' },
] as const;

// Primary region options
export const REGION_OPTIONS = [
  { value: 'uk', label: 'UK' },
  { value: 'eu', label: 'EU' },
  { value: 'us', label: 'US' },
  { value: 'apac', label: 'APAC' },
  { value: 'mena', label: 'Middle East & North Africa' },
  { value: 'latam', label: 'Latin America' },
  { value: 'africa', label: 'Africa' },
  { value: 'india', label: 'India' },
  { value: 'global', label: 'Global' },
] as const;

// Regulatory frameworks - seeded defaults, supports custom entries
export const REGULATORY_FRAMEWORKS = [
  { value: 'none', label: 'None / Not applicable', description: 'No regulatory requirements apply' },
  { value: 'gdpr', label: 'GDPR', description: 'General Data Protection Regulation' },
  { value: 'fca', label: 'FCA', description: 'Financial Conduct Authority' },
  { value: 'soc2', label: 'SOC 2', description: 'Service Organization Control 2' },
  { value: 'iso27001', label: 'ISO 27001', description: 'Information Security Management' },
  { value: 'hipaa', label: 'HIPAA', description: 'Health Insurance Portability and Accountability Act' },
  { value: 'fedramp', label: 'FedRAMP', description: 'Federal Risk and Authorization Management Program' },
  { value: 'pci_dss', label: 'PCI DSS', description: 'Payment Card Industry Data Security Standard' },
  { value: 'nist', label: 'NIST', description: 'National Institute of Standards and Technology' },
] as const;

// Risk sensitivity options - affects evaluation criteria and content scoring
export const RISK_SENSITIVITY_OPTIONS = [
  { value: 'risk_averse', label: 'Risk-Averse', description: 'Conservative, compliance-focused buyer' },
  { value: 'moderate', label: 'Moderate', description: 'Balanced approach to risk and innovation' },
  { value: 'innovation_led', label: 'Innovation-Led', description: 'Open to new approaches and technologies' },
] as const;

// Currency options
export const CURRENCY_OPTIONS = [
  { value: 'GBP', label: 'GBP', symbol: '£' },
  { value: 'USD', label: 'USD', symbol: '$' },
  { value: 'EUR', label: 'EUR', symbol: '€' },
  { value: 'AUD', label: 'AUD', symbol: 'A$' },
  { value: 'CAD', label: 'CAD', symbol: 'C$' },
  { value: 'CHF', label: 'CHF', symbol: 'CHF' },
  { value: 'INR', label: 'INR', symbol: '₹' },
  { value: 'AED', label: 'AED', symbol: 'د.إ' },
  { value: 'SAR', label: 'SAR', symbol: '﷼' },
  { value: 'QAR', label: 'QAR', symbol: 'QR' },
  { value: 'ZAR', label: 'ZAR', symbol: 'R' },
  { value: 'BRL', label: 'BRL', symbol: 'R$' },
  { value: 'MXN', label: 'MXN', symbol: 'MX$' },
  { value: 'SGD', label: 'SGD', symbol: 'S$' },
  { value: 'HKD', label: 'HKD', symbol: 'HK$' },
  { value: 'JPY', label: 'JPY', symbol: '¥' },
  { value: 'CNY', label: 'CNY', symbol: '¥' },
  { value: 'KRW', label: 'KRW', symbol: '₩' },
  { value: 'NZD', label: 'NZD', symbol: 'NZ$' },
] as const;

// Helper functions
export function getCurrencySymbol(currency: string): string {
  const found = CURRENCY_OPTIONS.find(c => c.value === currency);
  return found?.symbol || '£';
}

export function isFieldEditable(status: string, field: keyof typeof STATUS_EDITABILITY.draft): boolean {
  const rules = STATUS_EDITABILITY[status];
  if (!rules) return true; // Default to editable if status not found
  return rules[field];
}

export function getStatusLabel(status: string): string {
  const found = PROJECT_STATUS_OPTIONS.find(s => s.value === status);
  return found?.label || status;
}

export function getPriorityColor(priority: string): string {
  const found = PRIORITY_OPTIONS.find(p => p.value === priority);
  return found?.color || 'text-foreground';
}

// Type exports
export type IndustryValue = typeof INDUSTRY_OPTIONS[number]['value'];
export type BuyerTypeValue = typeof BUYER_TYPE_OPTIONS[number]['value'];
export type OpportunityTypeValue = typeof OPPORTUNITY_TYPE_OPTIONS[number]['value'];
export type RfpSourceValue = typeof RFP_SOURCE_OPTIONS[number]['value'];
export type ProjectStatusValue = typeof PROJECT_STATUS_OPTIONS[number]['value'];
export type PriorityValue = typeof PRIORITY_OPTIONS[number]['value'];
export type RegionValue = typeof REGION_OPTIONS[number]['value'];
export type RegulatoryFrameworkValue = typeof REGULATORY_FRAMEWORKS[number]['value'];
export type CurrencyValue = typeof CURRENCY_OPTIONS[number]['value'];
export type RiskSensitivityValue = typeof RISK_SENSITIVITY_OPTIONS[number]['value'];
